/** "Finish your setup" (backend plan 2026-10-29-dashboard-whats-next-setup §3): after the onboarding, What's next
 *  stays on the dashboard home until every step is done or hidden (WhatsNext's `card` mode renders nothing then).
 *
 *  Not shown while the onboarding is open (its banner leads to the Onboarding page, whose Ready screen has the
 *  list) or still being read, to a user without `tour_operator.read`, inside the Chrome extension's side panel, or
 *  for an account easy onboarding did not create: `409 not_easy` (and a 404) is final for a tenant, so it is
 *  remembered per tenant (sharedContext, like the onboarding's closed status) and later page loads skip the
 *  request. */
import { Box } from '@mui/material';
import * as React from 'react';

import { isDashboardChromeEmbed } from '../chromeEmbed';
import sharedContext from '../sharedContext';
import { ContextStore } from '../store/contextStore';
import { errorCode, OnboardingApiError } from './api';
import { useOnboardingStatus } from './OnboardingStatus';
import WhatsNext from './WhatsNext';

/** sharedContext key: the tenant id that has no setup steps (not created by easy onboarding). */
export const UNAVAILABLE_KEY = 'setup_steps_unavailable_top';

export default function FinishSetupCard() {
    const { tourOperator, hasPerm } = React.useContext(ContextStore) as any;
    const { status } = useOnboardingStatus();
    const topId = tourOperator?.id ?? null;
    const [unavailable, setUnavailable] = React.useState(() => topId != null && sharedContext.get(UNAVAILABLE_KEY) === topId);

    const refused = React.useCallback((e: unknown) => {
        if (topId != null && e instanceof OnboardingApiError && (errorCode(e) === 'not_easy' || e.status === 404)) {
            sharedContext.set(UNAVAILABLE_KEY, topId);
            setUnavailable(true);
        }
    }, [topId]);

    if (!hasPerm?.('tour_operator', 'read') || unavailable || status === 'open' || status === 'loading' || isDashboardChromeEmbed()) {
        return null;
    }
    return (
        <Box sx={{ mb: 3.25 }}>
            <WhatsNext
                card
                title="Finish your setup"
                testId="finish-setup"
                intro="A few steps are left to make your messages and your guests' pages complete."
                onRefused={refused}
            />
        </Box>
    );
}
